import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

export default function OnboardingPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<'wzap' | 'own' | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!selected || !user) return;
    setSaving(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          connection_type: selected,
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) {
        setError('Erro ao salvar: ' + updateError.message);
        setSaving(false);
        return;
      }

      await refreshProfile();
      setSaving(false);
      navigate('/dashboard');
    } catch (err) {
      setError('Erro inesperado.');
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    if (!user) {
      navigate('/dashboard');
      return;
    }
    setSaving(true);
    try {
      await supabase
        .from('profiles')
        .update({
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      await refreshProfile();
    } catch (err) {
      // Silently continue even if save fails
    }
    setSaving(false);
    navigate('/dashboard');
  };

  // If already onboarded, redirect
  if (profile?.onboarding_completed) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-background-50 dark:bg-background-100 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mx-auto mb-4">
            <i className="ri-whatsapp-fill text-white text-2xl" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
            Bem-vindo ao WZap PRO!
          </h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400 max-w-md mx-auto">
            Antes de começar, escolha como deseja conectar seu WhatsApp.
            Você pode mudar isso depois nas configurações.
          </p>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div
            onClick={() => setSelected('wzap')}
            className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
              selected === 'wzap'
                ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-500/10 shadow-sm'
                : 'border-background-200 dark:border-background-300 bg-white dark:bg-background-100 hover:border-background-300 dark:hover:border-background-400'
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-4">
              <i className="ri-flashlight-line text-2xl text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
              Usar WhatsApp WZAP
            </h3>
            <p className="text-xs text-foreground-500 dark:text-foreground-400 leading-relaxed mb-3">
              Mais simples e rápido. Nós gerenciamos toda a infraestrutura de envio.
              Ideal para quem quer começar agora sem burocracia.
            </p>
            <ul className="text-xs text-foreground-500 space-y-1">
              <li><i className="ri-check-line text-emerald-500 mr-1" />Setup em 2 minutos</li>
              <li><i className="ri-check-line text-emerald-500 mr-1" />Sem necessidade de aprovação Meta</li>
              <li><i className="ri-check-line text-emerald-500 mr-1" />Créditos de envio pré-pagos</li>
            </ul>
            {selected === 'wzap' && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-check-line text-white text-sm" />
              </div>
            )}
          </div>

          <div
            onClick={() => setSelected('own')}
            className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
              selected === 'own'
                ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-500/10 shadow-sm'
                : 'border-background-200 dark:border-background-300 bg-white dark:bg-background-100 hover:border-background-300 dark:hover:border-background-400'
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mb-4">
              <i className="ri-key-2-line text-2xl text-accent-600 dark:text-accent-400" />
            </div>
            <h3 className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
              Minha API Oficial Meta
            </h3>
            <p className="text-xs text-foreground-500 dark:text-foreground-400 leading-relaxed mb-3">
              Você usa seu próprio App Meta Business. Mais controle, ideal para empresas
              que já têm a API configurada ou precisam de compliance.
            </p>
            <ul className="text-xs text-foreground-500 space-y-1">
              <li><i className="ri-check-line text-emerald-500 mr-1" />Controle total da API</li>
              <li><i className="ri-check-line text-emerald-500 mr-1" />Templates aprovados por você</li>
              <li><i className="ri-check-line text-emerald-500 mr-1" />Pagamento direto à Meta</li>
            </ul>
            {selected === 'own' && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-check-line text-white text-sm" />
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3 mb-4">
            <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!selected || saving}
            className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold px-8 py-3 rounded-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Salvando...</>
            ) : (
              <><i className="ri-arrow-right-line" />Começar a Usar</>
            )}
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className="w-full sm:w-auto text-sm text-foreground-500 hover:text-foreground-700 dark:text-foreground-400 dark:hover:text-foreground-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Pular — Decido depois
          </button>
        </div>

        <p className="text-center text-[10px] text-foreground-400 mt-6">
          Você pode alterar esta configuração a qualquer momento em Configurações &gt; WhatsApp
        </p>
      </div>
    </div>
  );
}